import { defineStore } from "pinia";
import { isDevAuth, DEV_USER } from "@/plugins/auth";
import { setSentryUser, clearSentryUser } from "@/plugins/sentry";
import type { Subscription } from "@/types";

const STORAGE_KEY = "hoofvision_cached_user";
const SUBSCRIPTION_CLAIM = "https://hoofvision.app/subscription";

function toHex(str: string): string {
  return Array.from(new TextEncoder().encode(str))
    .map(b => b.toString(16).padStart(2, "0"))
    .join("");
}

export interface CachedUser {
  sub: string;
  name?: string;
  nickname?: string;
  email?: string;
  picture?: string;
  subscription?: Subscription;
  cachedAt: number;
}

interface AuthState {
  cachedUser: CachedUser | null;
  isOnline: boolean;
}

function loadFromStorage(): CachedUser | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
    if (isDevAuth) {
      return {
        ...DEV_USER,
        cachedAt: Date.now(),
      };
    }
    return null;
  } catch {
    return null;
  }
}

function saveToStorage(user: CachedUser | null): void {
  if (user) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
  } else {
    localStorage.removeItem(STORAGE_KEY);
  }
}

export const useAuthStore = defineStore("auth", {
  state: (): AuthState => ({
    cachedUser: loadFromStorage(),
    isOnline: navigator.onLine,
  }),
  getters: {
    user(): CachedUser | null {
      return this.cachedUser;
    },
    hasValidCache(): boolean {
      return this.cachedUser !== null;
    },
    databaseName(): string | null {
      if (!this.cachedUser?.sub) {
        return null;
      }
      return `userdb-${toHex(this.cachedUser.sub)}`;
    },
  },
  actions: {
    init() {
      window.addEventListener("online", () => this.setOnline(true));
      window.addEventListener("offline", () => this.setOnline(false));
    },

    cacheUser(auth0User: {
      sub?: string;
      name?: string;
      nickname?: string;
      email?: string;
      picture?: string;
      [SUBSCRIPTION_CLAIM]?: Subscription;
    }) {
      if (!auth0User.sub) {
        return;
      }

      const subscription: Subscription = auth0User[SUBSCRIPTION_CLAIM] ?? {
        plan: "free",
        status: "active",
      };

      const cached: CachedUser = {
        sub: auth0User.sub,
        name: auth0User.name,
        nickname: auth0User.nickname,
        email: auth0User.email,
        picture: auth0User.picture,
        subscription,
        cachedAt: Date.now(),
      };

      this.cachedUser = cached;
      saveToStorage(cached);
      setSentryUser(cached.sub, cached.email);
    },

    clearUser() {
      this.cachedUser = null;
      saveToStorage(null);
      clearSentryUser();
    },

    setOnline(online: boolean) {
      this.isOnline = online;
    },
  },
});
